<script>
	import { mapGetters, mapState } from "vuex";

	export default {
		data() {
			return {
				totalExclTax: "0,00",
				totalInclTax: "0,00",
				totalVat: "0,00"
			};
		},
		computed: {
			...mapGetters(["vats", "headerByKey", "totals", "searchAllRows", "currency", "currencySpacing"]),
			...mapState({
				vatsValues: state => state.vat.vatsValues,
				downpayment: state => state.downpayment.downpayment,
				table: state => state.billing.table,
				backgroundColor: state => state.settings.accentColor.background,
				foregroundColor: state => state.settings.accentColor.foreground,
				documentId: state => state.app.documentId,
				lastDocument: state => state.home.lastDocument,
				isEditable: state => state.home.isEditable,
				is_superadmin: state => state.home.is_superadmin,
				vatName: state => state.vat.vatName,
				isRecurringInvoice: state => state.recurring.isRecurringInvoice,
			}),
			unitSpacing() {
				return {
					"padding-right": `${this.currencySpacing * 6}px`
				};
			},
			selectedVatValue: {
				get() {
					return this.$store.state.vat.selectedVatValue;
				},
				set(value) {
					this.$store.commit("selectedVatValue", value);
				}
			},
			couponCode: {
				get() {
					return this.$store.state.vat.couponCode;
				},
				set(value) {
					this.$store.commit("couponCode", value);
				}
			},
			couponCodeValue: {
				get() {
					return this.$store.state.vat.couponCodeValue;
				},
				set(value) {
					this.$store.commit("couponCodeValue", value);
				}
			},
			couponCodeAmount: {
				get() {
					return this.$store.state.vat.couponCodeAmount;
				},
				set(value) {
					this.$store.commit("couponCodeAmount", value);
				}
			},
			couponCodePercent: {
				get() {
					return this.$store.state.vat.couponCodePercent;
				},
				set(value) {
					this.$store.commit("couponCodePercent", value);
				}
			},
			couponCodeType: {
				get() {
					return this.$store.state.vat.couponCodeType;
				},
				set(value) {
					this.$store.commit("couponCodeType", value);
				}
			},
			calculate() {
				const { selectedVatValue, vats, totals, downpayment, couponCodeValue, currency } = this;

				return {
					selectedVatValue,
					vats,
					totals,
					downpayment,
					couponCodeValue,
					currency
				};
			},
			vatsMonitoring() {
				const { downpayment, table } = this;

				return { downpayment, table };
			},
			translatedVats() {
				return this.vats.map(vat => ({
					value: vat.value,
					text: vat.text.includes('{vatName}') 
						? this.$t(vat.text, { vatName: this.vatName })
						: this.$t(vat.text)
				}));
			}
		},
		watch: {
			vatsMonitoring: {
				handler() {
					this.$nextTick(async () => {
						const entries = this.searchAllRows("entry");
						const vats = entries.map(entry => entry.data.vatPercentage);
						const vats2 = this.downpayment?.table
							? Object.values(this.downpayment.table).map(entry => entry?.data?.totals?.taxes?.vat)
							: // eslint-disable-next-line vue/script-indent
							  [];
						const vatValueBilling = await this.$store.dispatch("checkVatValues", vats);
						const vatValueDownpayments = await this.$store.dispatch("checkDownpaymentVatValues", vats2);
						const vatExist = this.vats.find(vat => vat.value === vatValueBilling);

						if (
							((typeof vatValueBilling !== "undefined" && typeof vatValueDownpayments === "undefined") ||
								vatValueBilling === vatValueDownpayments) &&
							vatExist
						) {
							this.$store.commit("selectedVatValue", vatValueBilling);
						} else {
							this.$store.commit("selectedVatValue", null);
						}
					});
				},
				deep: true,
				immediate: true
			},
			selectedVatValue(newValue) {
				this.$store.commit("selectedVatValue", newValue);

				const vatPercentageHeader = this.headerByKey("vat_percent");

				if (newValue === null) {
					// Reset the discount when switching to 'TVA multiple'
					this.couponCode = "";
					this.couponCodeValue = "0,00";

					if (vatPercentageHeader.enabled === false) {
						this.$store.commit("enableHeaderByKey", "vat_percent");
					}
				}

				// Exonération de TVA
				if (newValue === 0 && vatPercentageHeader.enabled === true) {
					this.$store.commit("disableHeaderByKey", "vat_percent");
				}
			},
			calculate: {
				handler() {
					const _tableExclTax = this.convertStringToNumber(this.totals.table.exclTax);
					const _tableVat = this.convertStringToNumber(this.totals.table.vat);
					const _downpaymentExclTax = this.convertStringToNumber(this.downpayment.totals.exclTax) / 100;
					const _downpaymentVat = this.convertStringToNumber(this.downpayment.totals.vat / 100);

					this.totalExclTax = _tableExclTax - _downpaymentExclTax;
					let _couponCodeValue = 0;

					if (String(this.couponCodeValue).includes("%")) {
						this.couponCodeType = "%";
					} else {
						if (String(this.couponCodeValue).includes(this.currency)) {
							this.couponCodeType = this.currency;
						}
					}

					if (this.couponCodeType === "%") {
						this.couponCodePercent =
							this.convertStringToNumber(this.couponCodeValue) !== 0
								? this.convertStringToNumber(this.couponCodeValue)
								: null;
						this.couponCodeAmount = null;
						this.couponCodeType = "%";

						_couponCodeValue = this.convertPercentageToAmount(this.totalExclTax, this.couponCodeValue);
					} else {
						this.couponCodeAmount =
							this.convertStringToNumber(this.couponCodeValue) !== 0
								? this.convertStringToNumber(this.couponCodeValue)
								: null;
						this.couponCodePercent = null;
						this.couponCodeType = this.currency;

						_couponCodeValue = this.couponCodeAmount;
					}

					this.totalExclTax -= this.convertStringToNumber(_couponCodeValue);

					//console.log("vat ",this.totalExclTax )

					const vatGeneralDiscount =
						this.convertStringToNumber(_couponCodeValue) * (this.selectedVatValue / 100);

					this.totalVat = this.convertStringToNumber(_tableVat - _downpaymentVat, 5) - vatGeneralDiscount;

					this.totalInclTax = (Math.round(this.totalExclTax * 100) / 100) + (Math.round(this.totalVat * 100) / 100);
				},
				deep: true,
				immediate: true
			}
		},
		methods: {
			formatterConvertNumberToString(value) {
				return this.convertNumberToString(value, 2);
			}
		}
	};
</script>

<template>
	<div>
		<div v-if="!is_superadmin && !isEditable" class="disable-table" />

		<b-table-simple>
			<b-tbody>
				<b-tr v-if="selectedVatValue !== null && !isRecurringInvoice">
					<b-td class="billing__cell">
						<label for="couponCode" class="sr-only">{{ $t('Remise HT') }}</label>
						<b-form-input
							id="couponCode"
							v-model.lazy="couponCode"
							:placeholder="$t('Remise HT')"
							class="billing__input"
						/>
					</b-td>
					<b-td class="billing__cell unit" :data-unit="couponCodeType" :style="unitSpacing">
						<label for="couponCodeValue" class="sr-only">{{ $t("Valeur de la remise") }}</label>
						<b-form-input
							id="couponCodeValue"
							v-model.lazy="couponCodeValue"
							lazy-formatter
							:formatter="formatterConvertNumberToString"
							placeholder="0"
							class="billing__input text-right"
							@focus="$event.target.select()"
						/>
						<b-tooltip
							placement="left"
							target="couponCodeValue"
							container="body"
						>
							{{ $t("Remise en % ou en valeur") }}
						</b-tooltip>
					</b-td>
				</b-tr>
				<b-tr class="bgc-grey">
					<b-td class="billing__cell text-right">
						<span class="billing__label text-medium">{{ $t("Total HT") }}</span>
					</b-td>
					<b-td class="billing__cell unit text-right" :style="unitSpacing" :data-unit="currency">
						<span class="billing__label">
							{{ totalExclTax | convertNumberToString(2) }}
						</span>
					</b-td>
				</b-tr>
				<b-tr class="bgc-grey">
					<b-td id="tvaLeftCell" class="billing__cell">
						<label for="selectedVatValue" class="sr-only">{{ $t("Taux de {vat} appliqué", { vat: vatName }) }}</label>
						<b-form-select
							v-model="selectedVatValue"
							class="billing__input pr-4 text-right hand"
							style="max-width: 172px"
						>
							<b-form-select-option
								v-for="(vat, index) in translatedVats"
								:key="index"
								:value="vat.value"
							>
								{{ vat.text }}
							</b-form-select-option>
						</b-form-select>
					</b-td>
					<b-td id="tvaRightCell" class="billing__cell unit text-right" :style="unitSpacing" :data-unit="currency">
						<span  class="billing__label">
							{{ totalVat | convertNumberToString(2) }}
						</span>
					</b-td>
				</b-tr>
				<b-tr
					class="sinao-bleu-petrole-bg document-total"
					:style="{
						'background-color': backgroundColor,
						color: foregroundColor
					}"
				>
					<b-td class="billing__cell text-right">
						<span class="billing__label text-medium">{{ $t("Total TTC") }}</span>
					</b-td>
					<b-td class="billing__cell unit text-right" :style="unitSpacing" :data-unit="currency">
						<span class="billing__label">
							{{ totalInclTax | convertNumberToString(2) }}
						</span>
					</b-td>
				</b-tr>
			</b-tbody>
		</b-table-simple>

		<b-tooltip placement="left" target="tvaLeftCell">
			{{ $t("Changer le taux de TVA") }}
		</b-tooltip>
	</div>
</template>

<style lang="scss" scoped>
	@import "../../scss/variables.scss";
	// @import '../../scss/sinao.scss';

	table {
		background-color: var(--white);
		// box-shadow: 0 1px 6px rgba(204, 204, 204, 0.51);

		td:not(.color-total) {
			border: thin solid $border-color;
			// border-top-color: transparent;
			// border-bottom-color: transparent;
		}
	}
</style>

<style scoped lang="scss">
	.billing__input.text-right option {
		direction: rtl;
	}

	.custom-select {
		background: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 16 16'%3e%3cpath fill='%231070d9' d='M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z'/%3e%3c/svg%3e") left  no-repeat !important;
		background-position-x: 1rem !important;
	}
	.toolTipLight {
		background-color: #ccc;
	}

	.document-total {
		td {
			border-left: 0px !important;
			border-right: 0px !important;
			border-bottom: 0px !important;

			&:first-child {
				border-bottom-left-radius: 6px;
			}

			&:last-child {
				border-bottom-right-radius: 6px;
			}
		}
	}
</style>